/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CountRemoteSessionsRequest,
  CountRemoteSessionsSecurity,
} from "../models/operations/countremotesessions.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRemoteSessionsCountQuery,
  prefetchRemoteSessionsCount,
  queryKeyRemoteSessionsCount,
  RemoteSessionsCountQueryData,
} from "./remoteSessionsCount.core.js";
export {
  buildRemoteSessionsCountQuery,
  prefetchRemoteSessionsCount,
  queryKeyRemoteSessionsCount,
  type RemoteSessionsCountQueryData,
};

export type RemoteSessionsCountQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * countRemoteSessions remoteSessions
 *
 * @remarks
 * Count the distinct people (user subjects) holding a live remote_session minted through one remote_session_client. Scoped like listRemoteSessions: only sessions whose issuer, client and remote issuer are reachable from the caller's project count; API-key and anonymous subjects do not. A client shared by several MCP servers counts people across all of them.
 */
export function useRemoteSessionsCount(
  request: CountRemoteSessionsRequest,
  security?: CountRemoteSessionsSecurity | undefined,
  options?: QueryHookOptions<
    RemoteSessionsCountQueryData,
    RemoteSessionsCountQueryError
  >,
): UseQueryResult<RemoteSessionsCountQueryData, RemoteSessionsCountQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRemoteSessionsCountQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * countRemoteSessions remoteSessions
 *
 * @remarks
 * Count the distinct people (user subjects) holding a live remote_session minted through one remote_session_client. Scoped like listRemoteSessions: only sessions whose issuer, client and remote issuer are reachable from the caller's project count; API-key and anonymous subjects do not. A client shared by several MCP servers counts people across all of them.
 */
export function useRemoteSessionsCountSuspense(
  request: CountRemoteSessionsRequest,
  security?: CountRemoteSessionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RemoteSessionsCountQueryData,
    RemoteSessionsCountQueryError
  >,
): UseSuspenseQueryResult<
  RemoteSessionsCountQueryData,
  RemoteSessionsCountQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRemoteSessionsCountQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRemoteSessionsCountData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      remoteSessionClientId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RemoteSessionsCountQueryData,
): RemoteSessionsCountQueryData | undefined {
  const key = queryKeyRemoteSessionsCount(...queryKeyBase);

  return client.setQueryData<RemoteSessionsCountQueryData>(key, data);
}

export function invalidateRemoteSessionsCount(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      remoteSessionClientId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessions", "count", ...queryKeyBase],
  });
}

export function invalidateAllRemoteSessionsCount(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessions", "count"],
  });
}
